/**
 * Form-editor helpers over the type-schema tree.
 * Not part of the validate contract (phone version guard).
 */

import {
  listObjectFields,
  type TypeSchema,
} from './type-schema'

export type ParsedTypeToken =
  | { kind: 'string' }
  | { kind: 'number' }
  | { kind: 'boolean' }
  | { kind: 'date' }
  | { kind: 'datetime' }
  | { kind: 'locale' }
  | { kind: 'timezone' }
  | { kind: 'media_url' }
  | { kind: 'https_url' }
  | { kind: 'object' }
  | { kind: 'enum'; values: string[] }
  | { kind: 'const'; value: string }
  | { kind: 'unknown'; token: string }

/** Parse a leaf type token string into a structured form widget hint. */
export function parseTypeToken(token: string): ParsedTypeToken {
  if (token.startsWith('enum:')) {
    return {
      kind: 'enum',
      values: token.slice('enum:'.length).split('|').filter(Boolean),
    }
  }
  if (token.startsWith('const:')) {
    return { kind: 'const', value: token.slice('const:'.length) }
  }
  switch (token) {
    case 'string':
    case 'number':
    case 'boolean':
    case 'date':
    case 'datetime':
    case 'locale':
    case 'timezone':
    case 'media_url':
    case 'https_url':
    case 'object':
      return { kind: token }
    default:
      return { kind: 'unknown', token }
  }
}

/**
 * Split a JSON path into segments.
 * Supports `a.b[0].c` and `a.b.0.c` (numeric segments treated as array indices).
 */
export function splitPath(path: string): Array<string | number> {
  if (!path) return []
  const segments: Array<string | number> = []
  const re = /([^[.\]]+)|\[(\d+)\]/g
  let match: RegExpExecArray | null
  while ((match = re.exec(path)) !== null) {
    if (match[1] !== undefined) {
      const part = match[1]
      if (/^\d+$/.test(part)) {
        segments.push(Number(part))
      } else {
        segments.push(part)
      }
    } else if (match[2] !== undefined) {
      segments.push(Number(match[2]))
    }
  }
  return segments
}

/**
 * Resolve the type-schema node at a data path.
 * Array indices step into the item schema. Returns null if the path is invalid.
 */
export function getSchemaAt(
  schema: TypeSchema,
  path: string,
): TypeSchema | null {
  const segments = splitPath(path)
  let node: TypeSchema = schema
  for (const seg of segments) {
    if (typeof seg === 'number') {
      if (!Array.isArray(node)) return null
      node = node[0]!
      continue
    }
    if (Array.isArray(node)) {
      return null
    }
    if (typeof node === 'string') return null
    const fields = listObjectFields(node)
    const field = fields.find((f) => f.key === seg)
    if (!field) return null
    node = field.schema
  }
  return node
}

/** True when schema is a leaf type token (not object/array structure). */
export function isLeafSchema(schema: TypeSchema): schema is string {
  return typeof schema === 'string'
}

/** True when schema describes an array of items. */
export function isArraySchema(schema: TypeSchema): schema is TypeSchema[] {
  return Array.isArray(schema)
}

/** Item schema for an array type-schema, or null. */
export function arrayItemSchema(schema: TypeSchema): TypeSchema | null {
  if (!Array.isArray(schema) || schema.length === 0) return null
  return schema[0]!
}

/**
 * Build a minimal empty value for a type-schema node (for "add row" / defaults).
 * Required object fields are filled; optional fields are omitted.
 * Arrays start empty. Leaf strings are "" (validators may still require non-empty on save).
 */
export function emptyValueForSchema(schema: TypeSchema): unknown {
  if (typeof schema === 'string') {
    return emptyValueForToken(schema)
  }
  if (Array.isArray(schema)) {
    return []
  }
  const out: Record<string, unknown> = {}
  for (const field of listObjectFields(schema)) {
    if (field.optional) continue
    out[field.key] = emptyValueForSchema(field.schema)
  }
  return out
}

function emptyValueForToken(token: string): unknown {
  const parsed = parseTypeToken(token)
  switch (parsed.kind) {
    case 'number':
      return 0
    case 'boolean':
      return false
    case 'enum':
      return parsed.values[0] ?? ''
    case 'const':
      return parsed.value
    case 'object':
      return {}
    case 'string':
    case 'date':
    case 'datetime':
    case 'locale':
    case 'timezone':
    case 'media_url':
    case 'https_url':
    case 'unknown':
      return ''
  }
}

/** Allowed values when schema is an `enum:a|b` token. */
export function enumValuesFromSchema(
  schema: TypeSchema | null | undefined,
): string[] {
  if (typeof schema !== 'string') return []
  const parsed = parseTypeToken(schema)
  return parsed.kind === 'enum' ? parsed.values : []
}

const ENUM_ALIASES: Record<string, Record<string, string>> = {
  direction: {
    incoming: 'inbox',
    received: 'inbox',
    inbox: 'inbox',
    收件箱: 'inbox',
    收件: 'inbox',
    outgoing: 'sent',
    out: 'sent',
    send: 'sent',
    sent: 'sent',
    已发送: 'sent',
    发件: 'sent',
    发件箱: 'sent',
    draft: 'draft',
    drafts: 'draft',
    草稿: 'draft',
    草稿箱: 'draft',
  },
  condition: {
    sunny: 'clear',
    clear: 'clear',
    晴: 'clear',
    'partly cloudy': 'partly_cloudy',
    'partly-cloudy': 'partly_cloudy',
    partly_cloudy: 'partly_cloudy',
    少云: 'partly_cloudy',
    overcast: 'cloudy',
    cloud: 'cloudy',
    cloudy: 'cloudy',
    多云: 'cloudy',
    rainy: 'rain',
    rain: 'rain',
    雨: 'rain',
  },
}

/**
 * Map a stored enum to an allowed schema value.
 * Empty values become the first allowed option. Known aliases (e.g. gmail
 * `incoming` → `inbox`) apply only when the alias target is allowed.
 * Unknown non-empty values stay as-is when `unknown` is `keep`.
 */
export function coerceEnumValue(
  raw: unknown,
  allowed: readonly string[],
  fieldKey?: string,
  opts?: { unknown?: 'first' | 'keep' },
): string {
  if (allowed.length === 0) {
    return typeof raw === 'string' ? raw : ''
  }
  const value = typeof raw === 'string' ? raw.trim() : ''
  if (allowed.includes(value)) return value
  if (value) {
    const lower = value.toLowerCase()
    if (allowed.includes(lower)) return lower
    const alias = fieldKey
      ? (ENUM_ALIASES[fieldKey]?.[value] ?? ENUM_ALIASES[fieldKey]?.[lower])
      : undefined
    if (alias && allowed.includes(alias)) return alias
    if (opts?.unknown === 'keep') return value
  }
  return allowed[0]!
}

/**
 * Walk a case package and coerce every present enum field to a schema value.
 * Missing optional fields stay omitted. Empty optional enums are dropped.
 */
export function normalizeEnumFields(
  value: unknown,
  schema: TypeSchema,
  fieldKey = '',
): unknown {
  if (typeof schema === 'string') {
    const parsed = parseTypeToken(schema)
    if (parsed.kind !== 'enum') return value
    return coerceEnumValue(value, parsed.values, fieldKey)
  }

  if (Array.isArray(schema)) {
    if (!Array.isArray(value)) return value
    const itemSchema = schema[0]
    if (itemSchema === undefined) return value
    let changed = false
    const next = value.map((item) => {
      const coerced = normalizeEnumFields(item, itemSchema, fieldKey)
      if (coerced !== item) changed = true
      return coerced
    })
    return changed ? next : value
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value
  }

  const rec = value as Record<string, unknown>
  let changed = false
  const next: Record<string, unknown> = { ...rec }
  for (const field of listObjectFields(schema)) {
    if (!(field.key in rec)) continue
    const childSchema = field.schema
    const childValue = rec[field.key]
    if (typeof childSchema === 'string') {
      const parsed = parseTypeToken(childSchema)
      if (parsed.kind === 'enum') {
        const empty =
          childValue == null ||
          (typeof childValue === 'string' && childValue.trim() === '')
        if (field.optional && empty) {
          delete next[field.key]
          changed = true
          continue
        }
        const coerced = coerceEnumValue(
          childValue,
          parsed.values,
          field.key,
          { unknown: 'keep' },
        )
        if (coerced !== childValue) {
          next[field.key] = coerced
          changed = true
        }
        continue
      }
    }
    const coerced = normalizeEnumFields(childValue, childSchema, field.key)
    if (coerced !== childValue) {
      next[field.key] = coerced
      changed = true
    }
  }
  return changed ? next : value
}
